import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import { dealAction as dealActionApi, fetchDeal, startThread } from "./services/brandModeServices";
import { EMPTY_VALUE, fmtMoney, fmtMoneyCompact, fullName } from "./brandUtils";
import { counterAnalysis, fmtDate, fmtDateRange, fmtLongRange, timeAgo } from "./brandDealUtils";
import {
  ActionBar,
  Avatar,
  BackLink,
  Badge,
  BmModal,
  Btn,
  CampaignContextBar,
  Card,
  ConfirmModal,
  DealStepper,
  ErrorState,
  Field,
  Input,
  InsightBox,
  Loading,
  Overline,
  TextArea,
} from "./ui/BrandUI";

const STEP_FOR_STATUS = { draft: 1, open: 1, in_contract: 2, in_progress: 3, completed: 4 };

const Cell = ({ label, chip, chipTone = "brand", children, right = false }) => (
  <div className={`flex flex-col gap-3 p-6 ${right ? "bg-bm-subtle" : "bg-white"}`}>
    <div className="flex items-center justify-between gap-3">
      <Overline>{label}</Overline>
      {chip && (
        <Badge tone={chipTone} className="uppercase">
          {chip}
        </Badge>
      )}
    </div>
    {children}
  </div>
);

const Bullets = ({ lines }) => (
  <ul className="flex flex-col gap-2 bm-body-m text-bm-ink">
    {(lines || []).map((l) => (
      <li key={l}>• {l}</li>
    ))}
  </ul>
);

const CounterBackModal = ({ deal, onSubmit, onClose, busy }) => {
  const counter = deal.counter || {};
  const offer = deal.offer || {};
  const [compensation, setCompensation] = useState(String(counter.compensation || offer.compensation || ""));
  const [exclusivityDays, setExclusivityDays] = useState(offer.exclusivityDays ?? 14);
  const [note, setNote] = useState("");
  return (
    <BmModal
      title={`Counter back to ${fullName(deal.athlete)}`}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Btn variant="secondary" className="flex-1" onClick={onClose}>Cancel</Btn>
          <Btn className="flex-1" disabled={busy} onClick={() => onSubmit({ compensation: Number(String(compensation).replace(/,/g, "")) || 0, exclusivityDays: Number(exclusivityDays) || 0, note })}>
            {busy ? "Sending..." : "Send revised offer"}
          </Btn>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="bm-body-m text-bm-muted">
          The athlete asked for {fmtMoney(counter.compensation)}. Your original was {fmtMoney(offer.compensation)}. Deliverables stay as countered unless you edit the offer separately.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Compensation">
            <Input prefix="$" inputMode="numeric" value={compensation} onChange={(e) => setCompensation(e.target.value.replace(/[^\d,]/g, ""))} />
          </Field>
          <Field label="Exclusivity window (days)">
            <Input type="number" min={0} max={365} value={exclusivityDays} onChange={(e) => setExclusivityDays(e.target.value)} />
          </Field>
        </div>
        <Field label="Note to the athlete">
          <TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain the revised terms..." />
        </Field>
      </div>
    </BmModal>
  );
};

const ReviewCounterPage = () => {
  const navigate = useNavigate();
  const { dealId } = useParams();
  const { refreshBadges, refreshCampaigns } = useBrandMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [counterOpen, setCounterOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchDeal(dealId, { fullPool: true });
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not load the counter offer");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const run = async (action, payload, successText) => {
    setBusy(action);
    try {
      await dealActionApi(dealId, action, payload);
      toast.success(successText);
      refreshBadges();
      refreshCampaigns();
      navigate(`/brand/campaigns/${data.deal.campaignId}`);
    } catch (err) {
      toast.error(err.message || "Could not update the deal");
    } finally {
      setBusy(null);
    }
  };

  const message = async () => {
    try {
      const thread = await startThread(data.deal.campaignId, data.deal.athleteId);
      navigate(`/brand/campaigns/${data.deal.campaignId}/messages?thread=${thread.id}`);
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
    }
  };

  if (error) return <div className="py-10"><ErrorState>{error}</ErrorState></div>;
  if (!data) return <Loading label="Loading counter offer..." />;

  const { deal, campaign, brand, marketRange } = data;
  const offer = deal.offer || {};
  const counter = deal.counter;
  const diff = deal.counterDiff || {};
  const athleteName = fullName(deal.athlete);
  const delta = (counter?.compensation || 0) - (offer.compensation || 0);

  if (!counter) {
    return (
      <div className="flex flex-col gap-5 pt-6">
        <BackLink to={`/brand/campaigns/${deal.campaignId}`}>Back to campaign</BackLink>
        <Card className="py-12 text-center">
          <p className="bm-body-m text-bm-muted">There is no pending counter on this deal. Its status is {deal.statusLabel.toLowerCase()}.</p>
          <Btn className="mt-4" variant="secondary" onClick={() => navigate(`/brand/deals/${dealId}`)}>Open deal</Btn>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to={`/brand/campaigns/${deal.campaignId}`}>Back to campaign</BackLink>
      <CampaignContextBar
        campaign={campaign}
        linkTo={`/brand/campaigns/${deal.campaignId}`}
        right={
          <span>
            {[fmtDateRange(campaign?.startDate, campaign?.endDate), campaign?.budget?.total ? `${fmtMoney(campaign.budget.total)} budget` : null, `${campaign?.athleteIds?.length || 0} athletes`]
              .filter(Boolean)
              .join("  ·  ")}
          </span>
        }
      />
      <DealStepper current={STEP_FOR_STATUS[campaign?.status] || 1} />

      <div className="flex flex-col gap-[6px]">
        <Overline brand>Deal flow</Overline>
        <h1 className="bm-h-l text-bm-ink">Review Counter Offer</h1>
        <p className="bm-body-l text-bm-muted">
          {athleteName} countered your original offer. Please review the terms below to accept, re-counter, or decline.
        </p>
      </div>

      <Card padding="p-0" className="overflow-hidden">
        <div className="grid md:grid-cols-2">
          <div className="flex items-center gap-3 border-b border-bm-border p-6">
            <div className="flex flex-col gap-2">
              <Overline>Your original offer</Overline>
              <span className="flex items-center gap-3">
                <Avatar initials={brand?.initials} size={32} tone="muted" />
                <span className="bm-h-s text-bm-ink">Sent on {fmtDate(offer.sentAt)}</span>
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 border-b border-bm-border bg-bm-subtle p-6">
            <div className="flex flex-col gap-2">
              <Overline brand>Athlete's counter</Overline>
              <span className="bm-h-s text-bm-ink">
                {athleteName} · {timeAgo(counter.at)}
              </span>
            </div>
            <Badge tone="passive" className="uppercase">Countered</Badge>
          </div>

          <Cell label="1. Total compensation">
            <span className="bm-num-xl text-bm-ink">{fmtMoney(offer.compensation)}</span>
            <span className="bm-body-s text-bm-muted">Flat fee payout upon milestone approvals.</span>
          </Cell>
          <Cell right label="1. Countered compensation" chip={diff.compensation}>
            <span className="bm-num-xl text-bm-ink">{fmtMoney(counter.compensation)}</span>
            <span className="bm-body-s text-bm-muted">
              {delta ? `Athlete requested a flat-fee ${delta > 0 ? "increase" : "decrease"} of ${fmtMoney(Math.abs(delta))}.` : "No change to compensation."}
            </span>
          </Cell>

          <Cell label="2. Required deliverables">
            <Bullets lines={(offer.deliverableLines || []).filter((l) => !l.startsWith("Brand may use"))} />
          </Cell>
          <Cell right label="2. Countered deliverables" chip={diff.deliverables} chipTone="dark">
            <Bullets lines={(counter.deliverableLines || []).filter((l) => !l.startsWith("Brand may use"))} />
          </Cell>

          <Cell label="3. Campaign duration">
            <span className="bm-label-l text-bm-ink">{fmtLongRange(offer.startDate, offer.endDate)}</span>
            <Overline className="mt-2">4. Competing brand exclusivity</Overline>
            <span className="bm-label-l text-bm-ink">
              {offer.exclusivityEnabled === false ? "No exclusivity" : `${offer.exclusivityDays}-day brand restriction after each post`}
            </span>
          </Cell>
          <Cell right label="3. Campaign duration">
            <span className="bm-body-m text-bm-muted">
              {fmtLongRange(counter.startDate || offer.startDate, counter.endDate || offer.endDate)}
              {diff.duration ? "" : " (No change)"}
            </span>
            <div className="mt-2 flex items-center gap-3">
              <Overline>4. Competing brand exclusivity</Overline>
              {diff.exclusivity && <Badge tone="attention">{diff.exclusivity}</Badge>}
            </div>
            <span className="bm-label-l text-bm-ink">{counter.exclusivityDays}-day brand restriction after each post</span>
          </Cell>

          <Cell label="5. Payment schedule & milestones">
            <ul className="flex flex-col gap-2 bm-label-l text-bm-ink">
              {(offer.milestones || []).map((m) => (
                <li key={m.id || m.label} className="flex items-center gap-3">
                  <span className="h-[6px] w-[6px] rounded-full bg-bm-ink" />
                  {fmtMoney(m.amount)} · {m.label}
                </li>
              ))}
            </ul>
          </Cell>
          <Cell right label="5. Adjusted payment schedule">
            <ul className="flex flex-col gap-2 bm-label-l text-bm-ink">
              {(counter.milestones || []).map((m) => (
                <li key={m.id || m.label} className="flex items-center gap-3">
                  <span className="h-[6px] w-[6px] rounded-full bg-bm-ink" />
                  {fmtMoney(m.amount)} · {m.label}
                </li>
              ))}
            </ul>
          </Cell>

          <div className="flex flex-col gap-2 bg-bm-subtle p-6 md:col-span-2">
            <Overline>Athlete's note</Overline>
            <p className="bm-body-m text-bm-ink">{counter.note ? `"${counter.note}"` : "No note was included with the counter."}</p>
          </div>
        </div>
      </Card>

      <InsightBox
        title="Shortlist Counter analysis"
        tiles={[
          {
            label: "CacheScore",
            value: deal.athlete?.cacheScore ?? EMPTY_VALUE,
            sub: deal.athlete?.natlPct && deal.athlete.natlPct < 100 ? `Top ${deal.athlete.natlPct}% national` : "national ranking",
          },
          {
            label: "Market range",
            value: marketRange ? `${fmtMoneyCompact(marketRange.min)} - ${fmtMoneyCompact(marketRange.max)}` : EMPTY_VALUE,
            sub: marketRange ? "rate card for similar deliverables" : "no rate card",
          },
          {
            label: "Exclusivity",
            value: `${counter.exclusivityDays} Days`,
            sub: counter.exclusivityDays < (offer.exclusivityDays || 0) ? "shorter than your offer" : "as offered",
          },
        ]}
      >
        {counterAnalysis({ deal, marketRange })}
      </InsightBox>

      <ActionBar
        left={
          <>
            <Btn onClick={() => run("counter-accept", {}, `Counter accepted. Send ${athleteName} the contract next.`)} disabled={!!busy}>
              {busy === "counter-accept" ? "Accepting..." : "Accept counter"}
            </Btn>
            <Btn variant="secondary" onClick={() => setCounterOpen(true)} disabled={!!busy}>Counter back</Btn>
            <Btn variant="ghost" onClick={message}>Message athlete</Btn>
          </>
        }
        right={
          <button type="button" onClick={() => setDeclineOpen(true)} className="bm-label-l text-bm-critical hover:underline">
            Decline
          </button>
        }
      />

      {counterOpen && (
        <CounterBackModal
          deal={deal}
          busy={busy === "counter-reply"}
          onClose={() => setCounterOpen(false)}
          onSubmit={(payload) => run("counter-reply", payload, `Revised offer sent to ${athleteName}.`)}
        />
      )}
      {declineOpen && (
        <ConfirmModal
          title={`Decline ${athleteName}'s counter?`}
          body="Declining closes this negotiation. The athlete is notified and the deal moves to Declined. You can still message them or start a new offer later."
          ackLabel="I want to decline this counter."
          confirmLabel="Decline counter"
          tone="danger"
          busy={busy === "counter-decline"}
          onConfirm={() => run("counter-decline", {}, "Counter declined.")}
          onClose={() => setDeclineOpen(false)}
        />
      )}
    </div>
  );
};

export default ReviewCounterPage;
